// Entry (ESM): wires modules and defines the custom element in ONE file.
import { ThemeEvents } from "@theme/events";
import { applyCustomizerFields } from "@theme/customizer-fields";
import { applyCustomizerCanvas } from "@theme/customizer-canvas";
import { applyCustomizerRender } from "@theme/customizer-render";
import { applyCustomizerActions } from "@theme/customizer-actions";
import { mountCanvasTabs } from "@theme/customizer-tabs";

class ProductCustomizer extends HTMLElement {
  revealDesignTab() {
    // Tabs are rendered next to media-gallery (outside <product-customizer>),
    // so we search in the nearest product section scope.
    const scope = this.closest("[data-section-id]") || this.closest(".product") || document;
    const tabsEl = scope.querySelector('.canvas-tabs[data-testid="media-gallery-canvas-tab"], [data-canvas-tabs]');
    if (!tabsEl) return;

    try {
      tabsEl.dispatchEvent(new CustomEvent("customizer:reveal-design"));
    } catch (_) {}
  }

  connectedCallback() {
    this._designObjects = this._designObjects || new Map(); // id -> { obj, state, left, top, hidden }
    this._editingDesignId = null;

    // Cleanup registry for all modules (abort/controllers/observers/unsubscribers)
    this._cleanups = this._cleanups || [];

    this.bindFieldsetEvents?.();
    this.bindOutsideClickHandler?.();
    this.populatePreselectedValues?.();
    this.initConditionalVisibility?.();
    this.watchRequiredFields?.();
    this.bindVariantRequiredRevalidation?.();

    this.initCanvas?.();
    this._stages = this.readStages?.() || [];

    // Actions entrypoint (composed by action modules)
    this.initActionDropdowns?.();
    this.initActionButtons?.();
    this.initGreekAction?.();
    this.initImageAction?.();

    this.updateDragStageOverlay?.();
    this.observeConditionalImageTriggers?.();

    // Mount canvas/media tabs once (tabs live in media snippet, not inside <product-customizer>)
    if (!window.__gsCanvasTabsMounted) {
      try {
        mountCanvasTabs(document);
      } catch (_) {}
      window.__gsCanvasTabsMounted = true;
    }

    // Optional: canvas preview out-of-view observer
    this.setupCanvasStickyObserver();

    // When any option changes, reveal the Design tab so customer can see the canvas preview.
    const revealDesign = () => {
      this.revealDesignTab?.();
      // Wait one frame so the tab DOM/state is applied, then re-check sticky.
      requestAnimationFrame(() => this._syncCanvasStickyNow?.());
    };
    this.addEventListener("change", revealDesign, true);
    this.addEventListener("input", revealDesign, true);

    this.registerCleanup(() => {
      this.removeEventListener("change", revealDesign, true);
      this.removeEventListener("input", revealDesign, true);
    });
  }

  disconnectedCallback() {
    this.runCleanups();

    // Backward-compat cleanup (in case some older code still uses these)
    this._abortAndClear("_actionsAbort");
    this._abortAndClear("_dropdownAbort");
    this._disconnectAndClear("_actionsObserver");
    this._abortAndClear("_visibilityAbort");
    this._abortAndClear("_requiredAbort");

    if (this._observedCheckboxes) {
      for (const { checkbox, handler } of this._observedCheckboxes.values()) {
        checkbox.removeEventListener("change", handler);
      }
      this._observedCheckboxes.clear();
    }

    this._disconnectAndClear("_canvasResizeObserver");
  }

  setupCanvasStickyObserver() {
    if (!this.hasAttribute("data-sticky-canvas-preview")) return;

    // Mobile only
    const mql = window.matchMedia("(max-width: 749px)");
    if (!mql.matches) return;

    const scope = this.closest("[data-section-id]") || this.closest(".product") || document;
    const canvasPreviewEl = scope.querySelector(".product-media__canvas");
    if (!canvasPreviewEl) return;

    const STICKY_CLASS = "canvas-preview--sticky";
    const EXPANDED_CLASS = "canvas-preview--expanded";
    const PLACEHOLDER_CLASS = "canvas-preview-placeholder";
    const SENTINEL_CLASS = "canvas-preview-sentinel";
    const TOGGLE_BTN_ATTR = "data-canvas-sticky-toggle";

    const ICON_OPEN = `
      <svg width="24px" height="24px" stroke-width="1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000"><path d="M9 9L4 4M4 4V8M4 4H8" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M15 9L20 4M20 4V8M20 4H16" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M9 15L4 20M4 20V16M4 20H8" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M15 15L20 20M20 20V16M20 20H16" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
    `;

    const ICON_CLOSE = `
      <svg width="24px" height="24px" stroke-width="1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000"><path d="M20 20L15 15M15 15V19M15 15H19" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M4 20L9 15M9 15V19M9 15H5" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M20 4L15 9M15 9V5M15 9H19" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M4 4L9 9M9 9V5M9 9H5" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
    `;

    /* ---------------- placeholder (layout only) ---------------- */
    let placeholderEl = canvasPreviewEl.nextElementSibling;
    if (!placeholderEl || !placeholderEl.classList.contains(PLACEHOLDER_CLASS)) {
      placeholderEl = document.createElement("div");
      placeholderEl.className = PLACEHOLDER_CLASS;
      placeholderEl.style.cssText = "height:0;margin:0;padding:0;";
      canvasPreviewEl.after(placeholderEl);
    }

    /* ---------------- sentinel (flow marker, NOT absolute) ---------------- */
    let sentinelEl = placeholderEl.nextElementSibling;
    if (!sentinelEl || !sentinelEl.classList.contains(SENTINEL_CLASS)) {
      sentinelEl = document.createElement("div");
      sentinelEl.className = SENTINEL_CLASS;
      sentinelEl.style.cssText = "height:1px;margin:0;padding:0;";
      placeholderEl.after(sentinelEl);
    }

    const reservePlaceholder = () => {
      placeholderEl.style.height = `${canvasPreviewEl.offsetHeight}px`;
    };

    const releasePlaceholder = () => {
      placeholderEl.style.height = "0px";
    };

    const ensureToggleButton = () => {
      let btn = canvasPreviewEl.querySelector(`button[${TOGGLE_BTN_ATTR}]`);
      if (btn) return btn;

      btn = document.createElement("button");
      btn.type = "button";
      btn.className = "canvas-sticky-toggle";
      btn.setAttribute(TOGGLE_BTN_ATTR, "");
      btn.innerHTML = `${ICON_OPEN}<span>Show design</span>`;

      canvasPreviewEl.prepend(btn);

      btn.addEventListener("click", () => {
        canvasPreviewEl.classList.toggle(EXPANDED_CLASS);
        const expanded = canvasPreviewEl.classList.contains(EXPANDED_CLASS);
        btn.innerHTML = expanded ? `${ICON_CLOSE}<span>Hide design</span>` : `${ICON_OPEN}<span>Show design</span>`;
      });

      return btn;
    };

    const removeToggleButton = () => {
      canvasPreviewEl.querySelector(`button[${TOGGLE_BTN_ATTR}]`)?.remove();
    };

    const setStickyState = (shouldStick) => {
      const isSticky = canvasPreviewEl.classList.contains(STICKY_CLASS);
      if (shouldStick === isSticky) return;

      if (shouldStick) {
        reservePlaceholder();
        canvasPreviewEl.classList.add(STICKY_CLASS);
        ensureToggleButton();
      } else {
        canvasPreviewEl.classList.remove(STICKY_CLASS, EXPANDED_CLASS);
        removeToggleButton();
        releasePlaceholder();
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        // Sentinel above viewport => canvas fully scrolled past
        setStickyState(entry.boundingClientRect.top < 0);
      },
      { threshold: 0 },
    );

    observer.observe(sentinelEl);

    // Sync when switching tabs / options without scroll
    this._syncCanvasStickyNow = () => {
      const rect = sentinelEl.getBoundingClientRect();
      setStickyState(rect.top < 0);
    };

    requestAnimationFrame(() => this._syncCanvasStickyNow?.());

    this.registerCleanup?.(() => {
      observer.disconnect();
      canvasPreviewEl.classList.remove(STICKY_CLASS, EXPANDED_CLASS);
      removeToggleButton();
      releasePlaceholder();
      sentinelEl.remove();
      placeholderEl.remove();
      this._syncCanvasStickyNow = null;
    });
  }

  bindVariantRequiredRevalidation() {
    const scope = this.closest("[data-section-id]") || this.closest(".product") || document;

    if (this._variantRevalidateAbort) {
      try {
        this._variantRevalidateAbort.abort();
      } catch (_) {}
    }

    const ac = new AbortController();
    this._variantRevalidateAbort = ac;
    const { signal } = ac;

    const revalidate = () => {
      requestAnimationFrame(() => this.watchRequiredFields?.());
    };

    ["variant:selected", "variant:updated", "variant:change", "variant:changed", "variant:update"].forEach((name) => {
      scope.addEventListener(name, revalidate, { capture: true, signal });
    });

    this.registerCleanup?.(() => {
      try {
        ac.abort();
      } catch (_) {}
      if (this._variantRevalidateAbort === ac) this._variantRevalidateAbort = null;
    });
  }

  registerCleanup(fn) {
    if (typeof fn !== "function") return;
    this._cleanups = this._cleanups || [];
    this._cleanups.push(fn);
  }

  registerAbort(controllerKey, controller) {
    if (!controller || typeof controller.abort !== "function") return;
    this[controllerKey] = controller;
    this.registerCleanup(() => {
      try {
        controller.abort();
      } catch (_) {}
      if (this[controllerKey] === controller) this[controllerKey] = null;
    });
  }

  registerObserver(observerKey, observer) {
    if (!observer || typeof observer.disconnect !== "function") return;
    this[observerKey] = observer;
    this.registerCleanup(() => {
      try {
        observer.disconnect();
      } catch (_) {}
      if (this[observerKey] === observer) this[observerKey] = null;
    });
  }

  runCleanups() {
    const list = Array.isArray(this._cleanups) ? this._cleanups : [];
    this._cleanups = [];

    for (let i = list.length - 1; i >= 0; i--) {
      try {
        list[i]?.();
      } catch (_) {}
    }
  }

  _abortAndClear(key) {
    const controller = this[key];
    if (controller && typeof controller.abort === "function") controller.abort();
    this[key] = null;
  }

  _disconnectAndClear(key) {
    const obs = this[key];
    if (obs && typeof obs.disconnect === "function") obs.disconnect();
    this[key] = null;
  }
}

// Mixins
applyCustomizerFields(ProductCustomizer.prototype);
applyCustomizerCanvas(ProductCustomizer.prototype);
applyCustomizerRender(ProductCustomizer.prototype);
applyCustomizerActions(ProductCustomizer.prototype);

if (!customElements.get("product-customizer")) {
  customElements.define("product-customizer", ProductCustomizer);
}

export { ProductCustomizer };
